import Vue from 'vue';
import { roundAmount } from '@/views/sports/components/bet-slip/bet-slip-utils';
import store from '@/store';
import { LoginTypes } from '@/utils/enums';
import snq from 'snq';

const reducer = (acc, cur) => parseFloat(cur.value) + acc;
const isPercentage = tax => !!+tax.is_percentage;
const isAccumulative = tax => !!+tax.is_accumulative;
const taxOnly = tax => tax.tax_type === 'tax';
const feeOnly = tax => tax.tax_type === 'fee';

export default class TaxHelper {
  mustApplyTax = Vue.prototype.$isElectron && store.state.player.loginType !== LoginTypes.Player;

  terminalTaxInfo = {};

  setTerminalTaxInfo(terminalTaxInfo) {
    this.terminalTaxInfo = terminalTaxInfo;
  }

  taxes() {
    if (!Object.keys(this.terminalTaxInfo).length) {
      return [];
    }
    return this.terminalTaxInfo
      .filter(tax => !!parseFloat(tax.value))
      .filter(tax => !!parseFloat(tax.is_active))
      .filter(tax => tax.tax_wager_type === 'sportsbook');
  }

  winTaxes() {
    return this.taxes().filter(tax => tax.applies_to === 'win');
  }

  riskTaxes() {
    return this.taxes().filter(tax => tax.applies_to === 'risk');
  }

  // WIN -----------------------------------------------------------------
  winPercentageTax() {
    return (
      this.winTaxes()
        .filter(isPercentage)
        .filter(taxOnly)
        .reduce(reducer, 0) / 100
    );
  }

  winPercentageFee() {
    return (
      this.winTaxes()
        .filter(isPercentage)
        .filter(feeOnly)
        .reduce(reducer, 0) / 100
    );
  }

  winAccumulativeTax() {
    return this.winTaxes()
      .filter(isAccumulative)
      .filter(taxOnly)
      .reduce(reducer, 0);
  }

  winAccumulativeFee() {
    return this.winTaxes()
      .filter(isAccumulative)
      .filter(feeOnly)
      .reduce(reducer, 0);
  }

  // RISK ------------------------------------------------------------------
  riskPercentageTax() {
    return (
      this.riskTaxes()
        .filter(isPercentage)
        .filter(taxOnly)
        .reduce(reducer, 0) / 100
    );
  }

  riskPercentageFee() {
    return (
      this.riskTaxes()
        .filter(isPercentage)
        .filter(feeOnly)
        .reduce(reducer, 0) / 100
    );
  }

  riskAccumulativeTax() {
    return this.riskTaxes()
      .filter(isAccumulative)
      .filter(taxOnly)
      .reduce(reducer, 0);
  }

  riskAccumulativeFee() {
    return this.riskTaxes()
      .filter(isAccumulative)
      .filter(feeOnly)
      .reduce(reducer, 0);
  }

  totalRiskTaxesAndFeesAsString() {
    const percentages = parseFloat(this.riskPercentageTax()) + parseFloat(this.riskPercentageFee());
    const accumulatives = parseFloat(this.riskAccumulativeTax()) + parseFloat(this.riskAccumulativeFee());

    return this.stringBuilder(percentages, accumulatives);
  }
  totalWinTaxesAndFeesAsString() {
    const percentages = parseFloat(this.winPercentageTax()) + parseFloat(this.winPercentageFee());
    const accumulatives = parseFloat(this.winAccumulativeTax()) + parseFloat(this.winAccumulativeFee());

    return this.stringBuilder(percentages, accumulatives);
  }

  totalTaxIndic() {
    return {
      risk: this.totalRiskTaxesAndFeesAsString(),
      win: this.totalWinTaxesAndFeesAsString(),
    };
  }

  stringBuilder(percentages, accumulatives) {
    const currency = store.state.settings.terminalInfo.currency_symbol;

    percentages = snq(() => roundAmount(percentages * 100).replace('.00', ''));
    accumulatives = roundAmount(accumulatives);
    if (parseFloat(percentages) > 0 && parseFloat(accumulatives) > 0) {
      return `${percentages}% + ${currency} ${accumulatives}`;
    }

    if (parseFloat(percentages) > 0) {
      return `${percentages}%`;
    }

    if (parseFloat(accumulatives) > 0) {
      return `${currency} ${accumulatives}`;
    }
  }

  // STORE METHODS
  /**
   ((100*riskamount)-(100*riskaccumulativeTax)-(100*riskaccumulativeFee))
                       /
   (100+risktotalPercentageTax + risktotalPercentageFee)
   * @param input_risk_amount
   * @returns {string|*}
   */
  applyTaxToRiskInputHidden({ input_risk_amount }) {
    const final =
      (100 * input_risk_amount - 100 * this.riskAccumulativeFee() - 100 * this.riskAccumulativeTax()) /
      (100 + this.riskPercentageTax() * 100 + this.riskPercentageFee() * 100);
    return this.mustApplyTax ? roundAmount(final) : input_risk_amount;
  }

  applyTaxToWinInputHidden({ input_win_amount }) {
    const riskPercentageTax = this.riskPercentageTax();
    const final = input_win_amount * riskPercentageTax;
    return this.mustApplyTax ? roundAmount(final) : input_win_amount;
  }

  calculateTaxAndFeeFromWagerResponse(bets) {
    const toBets = (bets.Selections ? bets.Selections : bets);
    const totalWinTax = toBets.reduce(
      (acc, curr) => roundAmount(parseFloat(acc) + parseFloat(curr.total_tax_win || curr.TotalTaxWin)),
      0,
    );

    const totalRiskTax = toBets.reduce(
      (acc, curr) => roundAmount(parseFloat(acc) + parseFloat(curr.total_tax_risk || curr.TotalTaxRisk)),
      0,
    );

    const totalWinFee = toBets.reduce(
      (acc, curr) => roundAmount(parseFloat(acc) + parseFloat(curr.total_fee_win || curr.TotalFeeWin)),
      0,
    );

    const totalRiskFee = toBets.reduce(
      (acc, curr) => roundAmount(parseFloat(acc) + parseFloat(curr.total_fee_risk || curr.TotalFeeRisk)),
      0,
    );

    const totalWinTaxAndFee = roundAmount(parseFloat(totalWinTax) + parseFloat(totalWinFee));
    const totalRiskTaxAndFee = roundAmount(parseFloat(totalRiskTax) + parseFloat(totalRiskFee));

    return {
      totalWinTax,
      totalRiskTax,
      totalWinFee,
      totalRiskFee,
      totalWinTaxAndFee,
      totalRiskTaxAndFee,
    };
  }
  // COMMON METHODS
  totalRiskTaxAmount(riskWithoutTax, betCount) {
    return (
      parseFloat(roundAmount(riskWithoutTax * this.riskPercentageTax())) +
      parseFloat(roundAmount(this.riskAccumulativeTax() * betCount))
    );
  }
  totalRiskFeeAmount(riskWithoutTax, betCount) {
    return (
      parseFloat(roundAmount(riskWithoutTax * this.riskPercentageFee())) +
      parseFloat(roundAmount(this.riskAccumulativeFee() * betCount))
    );
  }
  // includes fee
  calculatedRiskTaxes(riskWithoutTax, betCount) {
    return roundAmount(
      this.totalRiskTaxAmount(parseFloat(riskWithoutTax), betCount) +
      this.totalRiskFeeAmount(parseFloat(riskWithoutTax), betCount),
    );
  }

  totalRiskCost(riskWithoutTax, betCount, totalRiskAmount) {
    return roundAmount(
      parseFloat(this.totalRiskTaxAmount(riskWithoutTax, betCount)) +
      parseFloat(this.totalRiskFeeAmount(riskWithoutTax, betCount)) +
      parseFloat(totalRiskAmount),
    );
  }

  totalWinTaxAmount(win, betCount) {
    return win * this.winPercentageTax() + this.winAccumulativeTax() * betCount;
  }

  totalWinFeeAmount(win, betCount) {
    return win * this.winPercentageFee() + this.winAccumulativeFee() * betCount;
  }
  // includes fee
  calculatedWinTaxes(win, betCount) {
    return roundAmount(this.totalWinTaxAmount(win, betCount) + this.totalWinFeeAmount(win, betCount));
  }
}
